import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { Button } from "./ui/button";
import {
  Send, X, Minus, Sparkles, Loader2, CheckCircle2, Phone, ShieldCheck,
} from "lucide-react";
import { track } from "../lib/analytics";

const STORAGE_KEY = "ascend_mentor_session";
const FIRST_GREETING =
  "Hey, I'm Maya — your Ascend Higher mentor. I'm here with you. I help people get unstuck, organize their life, and figure out the next right step. What feels most important right now?";
const FIRST_SUGGESTIONS = [
  "💰 Money",
  "💪 Health",
  "🧘 Spirituality",
  "❤️ Relationships",
  "📅 Daily routine",
];

// Pages where the floating mentor should NOT appear (in-app or sensitive flows).
const HIDDEN_PATHS = [
  "/onboarding",
  "/coach",
  "/coach/voice",
  "/dashboard",
  "/mentor",
  "/start",
  "/billing",
  "/welcome",
];

function loadSession() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

function saveSession(session) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch (e) {}
}

export default function MentorBubble() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false);
  const [messages, setMessages] = useState([]);
  const [suggestions, setSuggestions] = useState(FIRST_SUGGESTIONS);
  const [roadmap, setRoadmap] = useState(null);
  const [crisis, setCrisis] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [bubblePulse, setBubblePulse] = useState(true);
  const scrollRef = useRef(null);

  // Hide on private/in-app pages
  const shouldHide = HIDDEN_PATHS.some((p) => location.pathname.startsWith(p));

  // Boot session on mount
  useEffect(() => {
    if (shouldHide) return;
    const saved = loadSession();
    if (saved && Array.isArray(saved.messages) && saved.messages.length) {
      setMessages(saved.messages);
      setSessionId(saved.sessionId || null);
      setSuggestions(saved.suggestions || []);
      setRoadmap(saved.roadmap || null);
      setHasOpenedOnce(true);
      // Don't auto-open returning sessions — let user reopen on their terms
    } else {
      // Seed the welcome greeting
      setMessages([{ role: "assistant", content: FIRST_GREETING }]);
    }
    // First-visit auto-open after a short delay so the page can settle
    const t = setTimeout(() => {
      const session = loadSession();
      if (!session?.dismissed && !shouldHide) {
        // Only pulse — don't force-open. Less aggressive, more inviting.
        setBubblePulse(true);
      }
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Persist session on every change
  useEffect(() => {
    if (shouldHide) return;
    saveSession({
      messages,
      sessionId,
      suggestions,
      roadmap,
      dismissed: !open && hasOpenedOnce,
    });
  }, [messages, sessionId, suggestions, roadmap, open, hasOpenedOnce, shouldHide]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending, roadmap]);

  const openPanel = useCallback(() => {
    setOpen(true);
    setHasOpenedOnce(true);
    setBubblePulse(false);
    track("mentor_bubble_opened", { page: location.pathname });
  }, [location.pathname]);

  const closePanel = useCallback(() => {
    setOpen(false);
    track("mentor_bubble_closed");
  }, []);

  const send = async (text) => {
    const trimmed = (text || "").trim();
    if (!trimmed || sending) return;
    const next = [...messages, { role: "user", content: trimmed }];
    setMessages(next);
    setInput("");
    setSuggestions([]);
    setSending(true);
    try {
      const r = await axios.post(`${API}/public/coach-chat`, {
        messages: next,
        session_id: sessionId,
      });
      setSessionId(r.data.session_id);
      const reply = r.data.reply;
      const updated = [...next, { role: "assistant", content: reply }];
      setMessages(updated);
      setSuggestions(r.data.suggested_replies || []);
      if (r.data.roadmap) {
        setRoadmap(r.data.roadmap);
        track("mentor_roadmap_generated");
      }
      if (r.data.crisis) {
        setCrisis(true);
        track("mentor_crisis_redirect");
      }
      track("mentor_message_sent", { turn: next.length });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const friendly =
        e?.response?.status === 429
          ? "Let's slow down for a sec — try again in a minute."
          : typeof detail === "string"
          ? detail
          : "I lost the line for a second. Try once more?";
      setMessages((m) => [...m, { role: "assistant", content: friendly }]);
    } finally {
      setSending(false);
    }
  };

  if (shouldHide) return null;

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          data-testid="mentor-bubble"
          onClick={openPanel}
          aria-label="Talk to your Ascend mentor"
          className="fixed z-[60] bottom-5 right-5 sm:bottom-7 sm:right-7 group"
        >
          <div className="relative w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full overflow-hidden bg-[#eadfc9] shadow-[0_18px_50px_-15px_rgba(15,23,36,0.45)] border-2 border-white">
            <img
              src="/mentors/maya.png"
              alt="Maya, your AI mentor"
              className="w-full h-full object-cover"
              draggable={false}
            />
            {/* Online dot */}
            <span
              className="absolute bottom-1 right-1 w-3 h-3 rounded-full border-2 border-white"
              style={{ background: "#3e6b52" }}
            />
            {bubblePulse && !hasOpenedOnce && (
              <>
                <span
                  aria-hidden
                  className="absolute inset-0 rounded-full pointer-events-none"
                  style={{
                    boxShadow: "0 0 0 6px rgba(72,106,143,0.18), 0 0 0 18px rgba(72,106,143,0.06)",
                    animation: "ascendPulseRing 2.2s ease-out infinite",
                  }}
                />
              </>
            )}
          </div>
          {!hasOpenedOnce && (
            <div className="absolute -top-2 right-[80px] sm:right-[88px] bg-[#0f1724] text-white px-3 py-2 rounded-2xl rounded-br-sm shadow-lg whitespace-nowrap font-body text-[12px] hidden sm:block">
              Hey — got a sec? Tap me.
            </div>
          )}
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="mentor-panel"
          className="fixed z-[60] inset-x-0 bottom-0 sm:inset-auto sm:bottom-7 sm:right-7 sm:w-[400px] sm:max-w-[calc(100vw-32px)] sm:h-[640px] sm:max-h-[calc(100vh-56px)] flex flex-col bg-white sm:rounded-3xl shadow-[0_30px_80px_-20px_rgba(15,23,36,0.4)] border border-[#eadfc9] overflow-hidden h-[100dvh] animate-mentor-rise"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-[#eadfc9] bg-[#faf6ec]">
            <div className="relative w-10 h-10 rounded-full overflow-hidden border border-white shrink-0">
              <img src="/mentors/maya.png" alt="Maya" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-body text-[14px] font-semibold text-[#0f1724] leading-tight truncate">
                Maya · your AI mentor
              </p>
              <p className="font-body text-[11px] text-[#3e6b52] flex items-center gap-1 leading-tight">
                <span className="w-1.5 h-1.5 rounded-full bg-[#3e6b52]" /> Online · here with you
              </p>
            </div>
            <button
              onClick={closePanel}
              data-testid="mentor-panel-close"
              aria-label="Close"
              className="w-8 h-8 rounded-full flex items-center justify-center text-[#6b7280] hover:bg-white"
            >
              <X size={16} />
            </button>
          </div>

          {/* Trust strip */}
          <div className="px-4 py-2 bg-[#f7f3ea] border-b border-[#eadfc9]">
            <div className="flex items-start gap-2">
              <ShieldCheck size={11} className="text-[#3e6b52] mt-0.5 shrink-0" />
              <p className="font-body text-[10.5px] text-[#6b7280] leading-snug">
                Your details stay private — never sold or shared.{" "}
                <Link
                  to="/privacy-promise"
                  className="text-[#486a8f] underline underline-offset-2"
                >
                  How
                </Link>
                . Ascend is a life mentor, not therapy or crisis support.
              </p>
            </div>
          </div>

          {/* Scrollable conversation */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 font-body text-[14px] leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#486a8f] text-white rounded-br-sm"
                      : "bg-[#f7f3ea] text-[#0f1724] rounded-bl-sm"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-[#f7f3ea] rounded-2xl rounded-bl-sm px-3.5 py-3">
                  <div className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#486a8f] animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#486a8f] animate-bounce" style={{ animationDelay: "120ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#486a8f] animate-bounce" style={{ animationDelay: "240ms" }} />
                  </div>
                </div>
              </div>
            )}

            {/* Roadmap card */}
            {roadmap && (
              <div data-testid="mentor-roadmap-card" className="rounded-2xl border border-[#3e6b52]/40 bg-white p-4 my-2">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles size={14} className="text-[#3e6b52]" />
                  <p className="font-mono-eyebrow text-[10px] text-[#3e6b52]">YOUR FIRST ROADMAP</p>
                </div>
                <p className="font-display text-[18px] text-[#0f1724] leading-tight mb-1">
                  {roadmap.main_goal}
                </p>
                {roadmap.why_it_matters && (
                  <p className="font-body text-[12px] text-[#4b5563] italic mb-3">
                    {roadmap.why_it_matters}
                  </p>
                )}
                {roadmap.today_action && (
                  <div className="rounded-xl bg-[#faf6ec] border border-[#eadfc9] p-3 mb-3">
                    <p className="font-mono-eyebrow text-[10px] text-[#486a8f] mb-1">TODAY</p>
                    <p className="font-body text-[13px] text-[#0f1724] leading-snug">
                      {roadmap.today_action}
                    </p>
                  </div>
                )}
                {Array.isArray(roadmap.three_steps) && roadmap.three_steps.length > 0 && (
                  <div className="mb-3">
                    <p className="font-mono-eyebrow text-[10px] text-[#486a8f] mb-1.5">3 STEPS</p>
                    <ul className="space-y-1.5">
                      {roadmap.three_steps.map((s, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle2 size={13} className="text-[#3e6b52] mt-0.5 shrink-0" />
                          <span className="font-body text-[13px] text-[#2b3442] leading-snug">{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {roadmap.daily_habit && (
                  <p className="font-body text-[12px] text-[#2b3442] mb-1.5">
                    <span className="font-mono-eyebrow text-[10px] text-[#486a8f] mr-1.5">DAILY HABIT</span>
                    {roadmap.daily_habit}
                  </p>
                )}
                {roadmap.weekly_checkpoint && (
                  <p className="font-body text-[12px] text-[#2b3442] mb-1.5">
                    <span className="font-mono-eyebrow text-[10px] text-[#486a8f] mr-1.5">SUNDAY REVIEW</span>
                    {roadmap.weekly_checkpoint}
                  </p>
                )}
                {roadmap.encouragement && (
                  <p className="font-body text-[12.5px] text-[#0f1724] italic mt-3 pt-3 border-t border-[#eadfc9]">
                    "{roadmap.encouragement}"
                  </p>
                )}
                <Link
                  to="/start"
                  data-testid="mentor-roadmap-save-cta"
                  onClick={() => track("mentor_roadmap_save_cta_click")}
                  className="mt-4 block text-center w-full px-4 py-2.5 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white font-body text-[13px] font-medium"
                >
                  Save this & keep coaching me →
                </Link>
                <p className="font-body text-[10.5px] text-[#6b7280] text-center mt-2">
                  Free 3-day trial. Card required at Stripe checkout. Cancel anytime.
                </p>
              </div>
            )}

            {/* Crisis safety card */}
            {crisis && (
              <div data-testid="mentor-crisis-card" className="rounded-2xl border border-[#c0392b]/40 bg-[#fff7f6] p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Phone size={14} className="text-[#c0392b]" />
                  <p className="font-mono-eyebrow text-[10px] text-[#c0392b]">IMMEDIATE SUPPORT</p>
                </div>
                <p className="font-body text-[13px] text-[#0f1724] leading-relaxed mb-3">
                  If you're in danger or in crisis, please call or text <strong>988</strong> (US) — free, confidential, available 24/7. Outside the US, please use your local emergency line.
                </p>
                <a
                  href="tel:988"
                  className="block text-center w-full px-4 py-2.5 rounded-full bg-[#c0392b] hover:bg-[#a13225] text-white font-body text-[13px] font-medium"
                >
                  Call 988 now
                </a>
              </div>
            )}
          </div>

          {/* Suggested replies */}
          {suggestions && suggestions.length > 0 && !sending && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  data-testid={`mentor-suggestion-${i}`}
                  onClick={() => send(s)}
                  className="px-3 py-1.5 rounded-full bg-white border border-[#c5d2e3] hover:bg-[#f0f4fa] text-[#2b3442] font-body text-[12.5px] transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Composer */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send(input);
            }}
            className="px-3 py-3 border-t border-[#eadfc9] bg-white flex items-center gap-2"
          >
            <input
              data-testid="mentor-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Tell me what's on your mind…"
              className="flex-1 h-10 px-3 rounded-full border border-[#eadfc9] bg-[#faf6ec] focus:outline-none focus:border-[#486a8f] focus:bg-white font-body text-[14px]"
              maxLength={2000}
              autoFocus
            />
            <Button
              type="submit"
              data-testid="mentor-send-btn"
              disabled={!input.trim() || sending}
              className="h-10 w-10 p-0 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white shrink-0 disabled:opacity-40"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
